import React, { useEffect, useLayoutEffect, useRef } from 'react';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import BottomNav from './BottomNav';
import ScrollToTopButton from '../common/ScrollToTopButton';
// AppLayout Component
const AppLayout = ({
  children,
  currentView = 'dashboard',
  onNavigate,
  onNewPlan,
  topBarProps = {},
  isCreatingPlan = false,
  showTopBar = true,
  onMenuToggleRef = null,
  onShowPrivacy = null,
  onShowTerms = null,
  isCollapsed = false,
  onToggleCollapse = () => { },
  onSignOut = () => { },
  onAutoCollapse = () => { },
  user = null,
  navigationKey = 0
}) => {
  const mainRef = useRef(null);

  useLayoutEffect(() => {
    const scrollToTop = () => {
      mainRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    };

    scrollToTop();
    const frameId = window.requestAnimationFrame(scrollToTop);
    const timeoutId = window.setTimeout(scrollToTop, 80);
    const lateTimeoutId = window.setTimeout(scrollToTop, 240);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
      window.clearTimeout(lateTimeoutId);
    };
  }, [currentView, navigationKey]);

  // Expose toggle function via ref if provided (backwards compatibility or if needed)
  useEffect(() => {
    if (onMenuToggleRef) {
      onMenuToggleRef.current = onToggleCollapse;
    }
  }, [onMenuToggleRef, onToggleCollapse]);

  // Also expose via window for global access (fallback)
  useEffect(() => {
    window.__toggleSidebar = onToggleCollapse;
    return () => {
      delete window.__toggleSidebar;
    };
  }, [onToggleCollapse]);

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex">
      {/* Sidebar — wrapper reserves the sidebar's width in the flex layout
           so the main content area reflows naturally on expand/collapse. */}
      <div className={`${isCollapsed ? 'hidden' : 'flex'} md:flex flex-shrink-0 transition-all duration-300 ${
        isCreatingPlan
          ? 'md:w-16'
          : isCollapsed
            ? 'md:w-16'
            : 'md:w-64'
      }`}>
        <Sidebar
          currentView={currentView}
          onNavigate={onNavigate}
          onNewPlan={onNewPlan}
          isCollapsed={isCollapsed}
          onToggleCollapse={onToggleCollapse}
          isCreatingPlan={isCreatingPlan}
          onSignOut={onSignOut}
          onShowPrivacy={onShowPrivacy}
          onShowTerms={onShowTerms}
          autoCollapseOnNavigate={isCreatingPlan}
          onAutoCollapse={onAutoCollapse}
        />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 lg:ml-0">
        {/* Top Bar - only show if showTopBar is true */}
        {showTopBar && <TopBar {...topBarProps} onNavigate={onNavigate} onMenuToggle={topBarProps.onMenuToggle || onToggleCollapse} />}

        {/* Content */}
        <main
          ref={mainRef}
          data-app-main-scroll="true"
          className="flex-1 overflow-y-auto scrollbar-stable pb-20 md:pb-0"
        >
          {children}
        </main>
      </div>

      {/* Bottom Nav - Mobile Only */}
      <BottomNav
        currentView={currentView}
        onNavigate={onNavigate}
        onNewPlan={onNewPlan}
        isCreatingPlan={isCreatingPlan}
        onSignOut={onSignOut}
        onShowPrivacy={onShowPrivacy}
        onShowTerms={onShowTerms}
      />

      {!isCreatingPlan && <ScrollToTopButton targetSelector="main[data-app-main-scroll='true']" />}

    </div>
  );
};

export default AppLayout;
